import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

import ApplicationProgress from './components/ApplicationProgress';
import CompanyProfileStep from './components/CompanyProfileStep';
import ServiceOfferingsStep from './components/ServiceOfferingsStep';
import ComplianceDocumentationStep from './components/ComplianceDocumentationStep';
import ReviewSubmitStep from './components/ReviewSubmitStep';

const PartnerApplicationSubmission = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [applicationData, setApplicationData] = useState({
    companyProfile: {},
    serviceOfferings: {},
    complianceDocumentation: {}
  });

  const steps = [
    { id: 1, title: 'Company Profile', icon: 'Building2', description: 'Basic company information' },
    { id: 2, title: 'Service Offerings', icon: 'Briefcase', description: 'Services and capabilities' },
    { id: 3, title: 'Compliance & Documentation', icon: 'FileText', description: 'Required documents' },
    { id: 4, title: 'Review & Submit', icon: 'CheckCircle', description: 'Final review' }
  ];

  const handleStepComplete = (stepData) => {
    const stepKey = {
      1: 'companyProfile',
      2: 'serviceOfferings',
      3: 'complianceDocumentation'
    }?.[currentStep];

    if (stepKey) {
      setApplicationData(prev => ({
        ...prev,
        [stepKey]: { ...prev?.[stepKey], ...stepData }
      }));
    }

    if (currentStep < steps?.length) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleApplicationSubmit = () => {
    // Navigate to dashboard or confirmation page
    navigate('/user-access-dashboard');
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <CompanyProfileStep
            data={applicationData?.companyProfile}
            onNext={handleStepComplete}
            onBack={() => navigate('/public-registration-portal')}
          />
        );
      case 2:
        return (
          <ServiceOfferingsStep
            data={applicationData?.serviceOfferings}
            onNext={handleStepComplete}
            onBack={handleBack}
          />
        );
      case 3:
        return (
          <ComplianceDocumentationStep
            data={applicationData?.complianceDocumentation}
            onNext={handleStepComplete}
            onBack={handleBack}
          />
        );
      case 4:
        return (
          <ReviewSubmitStep
            applicationData={applicationData}
            onSubmit={handleApplicationSubmit}
            onBack={handleBack}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
      {/* Header */}
      <header className="glass-container border-b border-white/20 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Icon name="Shield" size={32} className="text-primary" />
              <div>
                <h1 className="text-2xl font-bold text-foreground">Partner Application</h1>
                <p className="text-sm text-muted-foreground">Complete your onboarding documentation</p>
              </div>
            </div>
            <Button
              variant="outline"
              iconName="X"
              onClick={() => navigate('/user-access-dashboard')}
            >
              Save & Exit
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="space-y-8">
          {/* Progress Indicator */}
          <ApplicationProgress steps={steps} currentStep={currentStep} />

          {/* Step Content */}
          <div className="glass-container rounded-2xl p-8">
            {renderStep()}
          </div>

          {/* Help Section */}
          <div className="glass-container rounded-xl p-6 bg-primary/5">
            <div className="flex items-start gap-3">
              <Icon name="HelpCircle" size={24} className="text-primary mt-0.5" />
              <div className="space-y-1">
                <h4 className="font-semibold text-foreground">Need Help?</h4>
                <p className="text-sm text-muted-foreground">
                  Our support team is available 24/7 to assist with your application. Contact us at support@roamsure.com or call +1 (800) 555-0123.
                </p>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default PartnerApplicationSubmission;